<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh, saving } = realData;
const auditPanel = ref(null);
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'commissions'),
    screen: computed(() => 'edit'),
  })
);

const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};

const asArray = value => (Array.isArray(value) ? value : []);

const money = (value, currency = 'BRL') => {
  if (value === undefined || value === null || value === '') return '—';

  if (typeof value === 'object') {
    if (value.formatted || value.display || value.label) {
      return value.formatted || value.display || value.label;
    }
    return money(
      value.amount ?? value.amount_cents ?? value.cents,
      value.currency || currency
    );
  }

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(number);
};

const date = value => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const shortDate = value => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
  }).format(parsed);
};

const readable = value =>
  String(value || '—')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const source = computed(() => model.detail?.source || {});
const metadata = computed(() => source.value.metadata || {});
const currency = computed(() => source.value.currency || 'BRL');
const currentVersion = computed(() => asArray(source.value.versions)[0] || {});
const commissionCode = computed(
  () =>
    firstValue(source.value, ['reference', 'code'], '') ||
    (model.detail?.id ? `COM-${model.detail.id}` : 'Comissão')
);
const propertyTitle = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.title',
      'imovel_listing.titulo',
      'listing.title',
      'listing.titulo',
      'property.title',
      'property.titulo',
      'imovel.title',
      'imovel.titulo',
      'metadata.property_title',
      'metadata.listing_title',
    ]) || '—'
);
const propertyAddress = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.address',
      'listing.address',
      'listing.endereco',
      'property.address',
      'property.endereco',
      'imovel.address',
      'imovel.endereco',
      'metadata.property_address',
      'metadata.listing_address',
    ]) || '—'
);
const mediaUrl = media => {
  if (typeof media === 'string') return media;

  return asArray(media)
    .map(item => {
      if (typeof item === 'string') return item;
      return firstValue(item, [
        'url',
        'src',
        'href',
        'image_url',
        'imageUrl',
        'media_url',
        'mediaUrl',
      ]);
    })
    .find(Boolean);
};
const propertyImage = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.cover_url',
      'imovel_listing.image_url',
      'listing.cover_url',
      'listing.image_url',
      'property.cover_url',
      'property.image_url',
      'imovel.cover_url',
      'imovel.image_url',
      'metadata.property_image_url',
    ]) ||
    mediaUrl(
      firstValue(source.value, [
        'imovel_listing.midias',
        'listing.midias',
        'property.midias',
        'imovel.midias',
      ])
    )
);
const saleAmount = computed(() =>
  money(
    firstValue(source.value, [
      'base_amount',
      'settled_base_amount',
      'versions.0.base_amount',
      'metadata.sale_amount',
      'metadata.property_value',
    ]),
    currency.value
  )
);
const status = computed(() => {
  const value = String(source.value.status || model.detail?.status || '');
  const labels = {
    active: 'Ativa',
    cancelled: 'Cancelada',
    draft: 'Rascunho',
    settled: 'Liquidada',
    superseded: 'Substituída',
  };

  return labels[value.toLowerCase()] || readable(value);
});
const statusClass = computed(() => {
  const value = String(source.value.status || '').toLowerCase();
  if (value === 'active' || value === 'settled') {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  }
  if (value === 'draft') return 'bg-violet-50 text-[#5a24c7] ring-violet-200';
  if (value === 'superseded' || value === 'cancelled') {
    return 'bg-slate-100 text-slate-600 ring-slate-200';
  }
  return 'bg-[#f7f5fb] text-[#68749a] ring-[#e7e1f0]';
});

const commissionAmount = computed(() =>
  money(
    firstValue(source.value, [
      'commission_amount',
      'versions.0.computed_amount',
      'settled_amount',
    ]),
    currency.value
  )
);
const settledAmount = computed(() =>
  money(
    firstValue(source.value, ['settled_amount', 'versions.0.settled_amount']),
    currency.value
  )
);
const percentage = computed(() => {
  const value = source.value.commission_percentage;
  return value === undefined || value === null || value === ''
    ? '—'
    : `${value}%`;
});
const dash = value =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === 'Não informado'
    ? '—'
    : value;
const sourceValue = paths => dash(firstValue(source.value, paths));
const sourceMoney = paths =>
  dash(money(firstValue(source.value, paths), currency.value));
const percentageNumber = value => {
  if (value === undefined || value === null || value === '') return null;

  const parsed = Number(String(value).replace('%', '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};
const percentageText = value =>
  percentageNumber(value) === null ? '—' : `${value}%`;
const percentageBarWidth = value => {
  const numericValue = percentageNumber(value);
  if (numericValue === null || numericValue <= 0) return 'w-0';

  const rounded = Math.min(100, Math.max(5, Math.round(numericValue / 5) * 5));
  const widths = {
    5: 'w-[5%]',
    10: 'w-[10%]',
    15: 'w-[15%]',
    20: 'w-[20%]',
    25: 'w-1/4',
    30: 'w-[30%]',
    35: 'w-[35%]',
    40: 'w-2/5',
    45: 'w-[45%]',
    50: 'w-1/2',
    55: 'w-[55%]',
    60: 'w-3/5',
    65: 'w-[65%]',
    70: 'w-[70%]',
    75: 'w-3/4',
    80: 'w-4/5',
    85: 'w-[85%]',
    90: 'w-[90%]',
    95: 'w-[95%]',
    100: 'w-full',
  };

  return widths[rounded];
};
const percentageDelta = participant => {
  if (!participant.hasComparison) return '';

  const value =
    participant.currentPercentageValue - participant.previousPercentageValue;
  if (value === 0) return 'Sem alteração';

  return `${value > 0 ? '+' : ''}${value.toLocaleString('pt-BR')} p.p.`;
};

const formFacts = computed(() => [
  {
    label: 'Percentual da comissão (%)',
    value: percentage.value,
    span: 'col-span-1',
  },
  {
    label: 'Valor bruto da comissão',
    value: commissionAmount.value,
    span: 'col-span-1',
  },
  {
    label: 'Despesas administrativas',
    value: sourceMoney([
      'administrative_expenses',
      'metadata.administrative_expenses',
    ]),
    span: 'col-span-1 col-start-1',
  },
  {
    label: 'Impostos (ISS)',
    value: sourceMoney(['tax_amount', 'metadata.tax_amount']),
    span: 'col-span-1',
  },
  {
    label: 'Outras deduções',
    value: sourceMoney(['other_deductions', 'metadata.other_deductions']),
    span: 'col-span-1',
  },
  {
    label: 'Total de deduções',
    value: sourceMoney([
      'deductions_total',
      'total_deductions',
      'metadata.deductions_total',
    ]),
    span: 'col-span-1',
  },
  {
    label: 'Valor liquidado',
    value: settledAmount.value,
    span: 'col-span-1',
  },
  {
    label: 'Forma de pagamento',
    value: sourceValue(['payment_method', 'metadata.payment_method']),
    span: 'col-span-1 col-start-1',
  },
  {
    label: 'Entrada / 1ª parcela',
    value: shortDate(
      firstValue(source.value, [
        'first_installment_at',
        'metadata.first_installment_at',
      ])
    ),
    span: 'col-span-1',
  },
  {
    label: 'Nº de parcelas',
    value: sourceValue(['installments_count', 'metadata.installments_count']),
    span: 'col-span-1',
  },
  {
    label: 'Periodicidade',
    value: sourceValue(['periodicity', 'metadata.periodicity']),
    span: 'col-span-1',
  },
  {
    label: 'Status da comissão',
    value: status.value,
    span: 'col-span-1 col-start-1',
  },
  {
    label: 'Data prevista da última parcela',
    value: shortDate(
      firstValue(source.value, [
        'last_installment_at',
        'metadata.last_installment_at',
      ])
    ),
    span: 'col-span-1',
  },
  {
    label: 'Observações internas',
    value: sourceValue(['notes', 'metadata.notes', 'metadata.observations']),
    span: 'col-span-2',
  },
]);

const rawParticipants = computed(() => {
  const values = [
    source.value.participants,
    metadata.value.participants,
    metadata.value.distribution,
    metadata.value.rateio,
  ];

  return values.find(Array.isArray) || [];
});
const participants = computed(() =>
  rawParticipants.value.map((participant, index) => {
    const name =
      firstValue(participant, ['name', 'beneficiary.name', 'user.name']) ||
      (participant.user_id
        ? `Usuário #${participant.user_id}`
        : 'Participante não informado');

    const currentPercentage = firstValue(participant, [
      'percentage',
      'share_percentage',
      'current_percentage',
      'new_percentage',
      'after_percentage',
      'comparison.current',
      'comparison.new',
    ]);
    const previousPercentage = firstValue(participant, [
      'previous_percentage',
      'previous_share_percentage',
      'old_percentage',
      'before_percentage',
      'comparison.previous',
      'comparison.before',
    ]);
    const currentPercentageValue = percentageNumber(currentPercentage);
    const previousPercentageValue = percentageNumber(previousPercentage);

    return {
      amount: money(
        firstValue(participant, [
          'amount',
          'commission_amount',
          'gross_amount',
        ]),
        currency.value
      ),
      deduction: money(
        firstValue(participant, ['deduction', 'deductions', 'discount_amount']),
        currency.value
      ),
      id: participant.id || participant.user_id || `${name}-${index}`,
      currentPercentage: percentageText(currentPercentage),
      currentPercentageValue,
      hasComparison:
        currentPercentageValue !== null && previousPercentageValue !== null,
      name,
      netAmount: money(
        firstValue(participant, ['net_amount', 'settled_amount', 'amount']),
        currency.value
      ),
      percentage: percentageText(currentPercentage),
      previousPercentage: percentageText(previousPercentage),
      previousPercentageValue,
      role: readable(
        firstValue(
          participant,
          ['role', 'kind', 'beneficiary_kind'],
          'Participante'
        )
      ),
    };
  })
);
const hasRateioComparison = computed(() =>
  participants.value.some(participant => participant.hasComparison)
);
const rateioPercentageTotal = computed(() => {
  const percentages = participants.value
    .map(participant => participant.currentPercentageValue)
    .filter(value => value !== null);

  if (!percentages.length) return null;

  return percentages.reduce((total, value) => total + value, 0);
});
const rateioTotal = computed(() => {
  if (rateioPercentageTotal.value === null) return '—';

  return `${rateioPercentageTotal.value.toLocaleString('pt-BR')}%`;
});
const rateioBalance = computed(() => {
  if (rateioPercentageTotal.value === null) return '—';

  const balance = Math.max(0, 100 - rateioPercentageTotal.value);
  return `${balance.toLocaleString('pt-BR')}%`;
});

const rawVersions = computed(() => {
  const values = [source.value.versions, source.value.audit, model.history];
  return values.find(value => asArray(value).length) || [];
});
const audit = computed(() =>
  rawVersions.value.slice(0, 6).map((event, index) => {
    const actor =
      firstValue(event, ['actor.name', 'user.name']) ||
      (event.actor_id ? `Usuário #${event.actor_id}` : '') ||
      firstValue(event, ['actor_type'], 'Sistema');
    const action = firstValue(event, [
      'change_kind',
      'label',
      'action',
      'event_type',
    ]);
    const labels = {
      activated: 'Acordo ativado',
      created: 'Comissão criada',
      published: 'Acordo publicado',
      settled: 'Comissão liquidada',
      updated: 'Alterações registradas',
    };

    return {
      actor,
      date: date(
        firstValue(event, [
          'recorded_at',
          'occurred_at',
          'created_at',
          'updated_at',
        ])
      ),
      id: event.id || event.version_number || `${actor}-${index}`,
      label: labels[String(action).toLowerCase()] || readable(action),
      status:
        status.value === '—'
          ? readable(firstValue(event, ['status', 'state'], 'Registrado'))
          : readable(firstValue(event, ['status', 'state'], status.value)),
    };
  })
);

const versionSummary = computed(() => {
  const count = asArray(source.value.versions).length;
  if (!count) return 'Nenhuma versão retornada pela API.';

  return `${count} ${count === 1 ? 'versão registrada' : 'versões registradas'}.`;
});

const availableActions = computed(() => model.actionOptions || []);
const actionLabel = action =>
  ({
    markPaid: 'Marcar como paga',
    markPayable: 'Marcar como pagável',
    publishAgreement: 'Publicar acordo',
  })[action] || action;

const performAction = async action => {
  await model.runAction(action);
};

const returnToDashboard = () => emit('navigate', { screen: 'dashboard' });
const scrollToAudit = () =>
  auditPanel.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
</script>

<template>
  <main
    data-imudei-visual-id="commission-edit-root"
    class="relative mx-auto -mt-2 w-full max-w-[1320px] px-5 pb-28 pt-[5px] text-[#101958]"
  >
    <header
      data-imudei-visual-id="commission-edit-header"
      class="flex min-h-[89px] flex-wrap items-start justify-between gap-4 xl:ml-[1.5px]"
    >
      <div>
        <p class="text-[10px] font-medium text-[#5a32bd]">
          Comissões <span class="mx-2 text-[#a5adc1]">›</span>
          {{ commissionCode }} <span class="mx-2 text-[#a5adc1]">›</span>
          Editar
        </p>
        <div class="mt-px flex flex-wrap items-center gap-3">
          <h1 class="text-[31px] font-bold tracking-[-0.04em] text-[#101958]">
            Editar comissão
          </h1>
          <span
            class="rounded-full px-3 py-1.5 text-[8px] font-medium ring-1"
            :class="statusClass"
          >
            {{ status }}
          </span>
        </div>
        <p class="mt-1 text-[10px] text-[#66739b]">
          Consulte os valores e o histórico imutável deste acordo de comissão.
        </p>
      </div>
      <div class="flex items-center gap-3 pt-6 xl:mr-[136px]">
        <button
          class="flex h-9 items-center gap-2 rounded-lg border border-[#d9d3e6] bg-white px-4 text-[9px] font-medium text-[#27315d] transition hover:border-[#ad92df] hover:text-[#5a24c7]"
          type="button"
          :disabled="!audit.length"
          @click="scrollToAudit"
        >
          <i class="i-lucide-history size-4" />
          Ver histórico de alterações
        </button>
        <button
          aria-label="Feed indisponível"
          class="flex h-9 items-center gap-2 rounded-lg border border-[#d9d3e6] bg-white px-4 text-[9px] text-[#68749a] disabled:cursor-not-allowed disabled:opacity-70"
          disabled
          type="button"
        >
          <i class="i-lucide-users-round size-3.5 text-[#5a24c7]" />
          Feed indisponível
        </button>
      </div>
    </header>

    <section
      v-if="loading && !model.detail"
      class="mt-5 grid min-h-[430px] place-items-center rounded-2xl border border-[#e8e4ef] bg-white"
    >
      <span class="text-center">
        <i
          class="i-lucide-loader-circle mx-auto size-7 animate-spin text-[#5a24c7]"
        />
        <p class="mt-3 text-[10px] text-[#66739b]">
          Carregando acordo de comissão…
        </p>
      </span>
    </section>

    <section
      v-else-if="error"
      class="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-center"
    >
      <i class="i-lucide-circle-alert mx-auto size-7 text-rose-500" />
      <h2 class="mt-3 text-[13px] font-bold text-[#101958]">
        Não foi possível carregar
      </h2>
      <p class="mt-2 text-[9px] text-[#66739b]">{{ error }}</p>
      <button
        class="mt-4 rounded-lg border border-rose-200 bg-white px-4 py-2 text-[9px] font-semibold text-rose-600"
        type="button"
        @click="refresh"
      >
        Tentar novamente
      </button>
    </section>

    <section
      v-else-if="!model.detail"
      class="mt-5 rounded-2xl border border-[#e8e4ef] bg-white p-8 text-center"
    >
      <i class="i-lucide-file-search mx-auto size-8 text-[#ad92df]" />
      <h2 class="mt-3 text-[13px] font-bold">Comissão não encontrada</h2>
      <p class="mt-2 text-[9px] text-[#66739b]">
        Selecione uma comissão existente para consultar seus dados reais e os
        participantes retornados pela API.
      </p>
      <button
        class="mt-4 rounded-lg border border-[#cfc2ff] px-4 py-2 text-[9px] font-semibold text-[#5a24c7]"
        type="button"
        @click="returnToDashboard"
      >
        Voltar para comissões
      </button>
    </section>

    <template v-else>
      <section
        class="mt-[10px] grid w-full grid-cols-[minmax(0,1fr)_305px] items-start gap-4 xl:ml-[1.5px] xl:w-[1294px]"
      >
        <section>
          <article
            data-imudei-visual-id="commission-edit-progress"
            class="flex min-h-[112px] flex-wrap items-center gap-4 rounded-2xl border border-[#e8e4ef] bg-white px-4 py-3"
          >
            <img
              v-if="propertyImage"
              :src="propertyImage"
              :alt="propertyTitle"
              class="size-[84px] shrink-0 rounded-xl object-cover"
            />
            <span
              v-else
              class="grid size-[84px] shrink-0 place-items-center rounded-xl bg-gradient-to-br from-[#37206e] via-[#5a24c7] to-[#7fc7ee] text-white"
            >
              <i class="i-lucide-building-2 size-8 opacity-90" />
            </span>
            <span class="min-w-0 flex-1">
              <small
                class="flex items-center gap-2 text-[8px] font-medium text-[#5a24c7]"
              >
                <i class="i-lucide-lock-keyhole size-3.5" />
                Venda vinculada
                <span class="text-[#66739b]">(não editável)</span>
              </small>
              <strong class="mt-1.5 block truncate text-[12px] font-semibold">
                {{
                  propertyTitle === '—'
                    ? 'Imóvel vinculado sem detalhes'
                    : propertyTitle
                }}
              </strong>
              <p class="mt-1 truncate text-[10px]">
                {{
                  propertyAddress === '—'
                    ? 'Dados do imóvel não retornados pela API.'
                    : propertyAddress
                }}
              </p>
              <small class="mt-0.5 block text-[8px] text-[#66739b]">
                Listing #{{ source.imovel_listing_id || '—' }} · Acordo #{{
                  model.detail.id
                }}
              </small>
            </span>
            <span class="ml-auto text-right">
              <small class="text-[8px] text-[#66739b]">Valor da venda</small>
              <strong
                class="mt-0.5 block text-[21px] font-medium text-emerald-600"
              >
                {{ saleAmount }}
              </strong>
              <small
                class="mt-0.5 flex items-center justify-end gap-2 text-[8px] text-[#66739b]"
              >
                <i class="i-lucide-file-check-2 size-3.5 text-[#5a24c7]" />
                {{ status }}
              </small>
            </span>
          </article>

          <article
            data-imudei-visual-id="commission-edit-steps"
            class="mt-[15px] grid min-h-[57px] grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-5 overflow-hidden rounded-lg border border-[#cdbdff] bg-violet-50/50 px-4"
          >
            <span class="flex items-center gap-3">
              <i
                class="grid size-7 place-items-center rounded-full border border-[#8c62ed] text-[#5a24c7]"
              >
                <span class="i-lucide-shield-check size-3.5" />
              </i>
              <span>
                <strong class="block text-[8px] text-[#5a24c7]"
                  >Registro versionado</strong
                >
                <small class="text-[7px] text-[#66739b]">{{
                  versionSummary
                }}</small>
              </span>
            </span>
            <span
              class="flex min-w-0 items-center gap-3 border-l border-violet-200 pl-5"
            >
              <i class="i-lucide-file-clock size-4 text-orange-500" />
              <span>
                <strong class="block text-[8px]">Versão atual</strong>
                <small class="text-[7px] text-[#66739b]">
                  {{ currentVersion.version_number || '—' }}
                </small>
              </span>
              <small
                class="min-w-0 flex-1 text-[7px] leading-[1.35] text-[#66739b]"
              >
                Dados de pontuação não retornados pela API.
              </small>
              <button
                class="min-w-[96px] rounded-lg border border-[#d4c8ed] bg-white/70 px-4 py-1.5 text-[8px] text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-70"
                disabled
                type="button"
              >
                Imutável
              </button>
            </span>
          </article>

          <article
            data-imudei-visual-id="commission-edit-form"
            class="mt-3 h-[309px] rounded-2xl border border-[#e8e4ef] bg-white px-5 py-4"
          >
            <h2 class="text-[13px] font-bold tracking-[-0.01em]">
              Dados da comissão
            </h2>
            <dl class="mt-2 grid grid-cols-4 gap-x-10 gap-y-2.5">
              <div
                v-for="fact in formFacts"
                :key="fact.label"
                class="min-w-0"
                :class="fact.span"
              >
                <dt class="text-[8px] font-medium text-[#68749a]">
                  {{ fact.label }}
                </dt>
                <dd
                  class="mt-1 flex h-7 items-center truncate rounded-md border border-[#e5e0ed] bg-[#fdfcff] px-2.5 text-[9px] font-medium text-[#27315d]"
                >
                  {{ fact.value }}
                </dd>
              </div>
            </dl>
          </article>

          <article
            class="mt-2 min-h-[240px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <header class="flex flex-wrap items-center justify-between gap-3">
              <span>
                <h2 class="text-[13px] font-bold">Rateio da comissão</h2>
                <p class="mt-1 text-[8px] text-[#68749a]">
                  Defina como o valor líquido será distribuído entre os
                  participantes.
                </p>
              </span>
              <span class="flex items-center gap-2">
                <i
                  class="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[8px] font-semibold text-emerald-700"
                  >Total do rateio: {{ rateioTotal }}</i
                >
                <i
                  class="rounded-full border border-[#d9c9ff] bg-violet-50 px-2 py-1 text-[8px] font-semibold text-[#5a24c7]"
                  >Saldo: {{ rateioBalance }}</i
                >
              </span>
            </header>
            <div v-if="participants.length" class="mt-4 overflow-x-auto">
              <div class="min-w-[660px]">
                <div
                  class="grid grid-cols-[minmax(190px,1.5fr)_120px_74px_100px_90px_108px] gap-2 px-2 text-[7px] font-bold text-[#7a85a2]"
                >
                  <span>Participante</span><span>Papel</span
                  ><span>Percentual</span><span>Valor bruto</span
                  ><span>Deduções</span><span>Valor líquido</span>
                </div>
                <div
                  v-for="participant in participants"
                  :key="participant.id"
                  class="mt-2 grid min-h-[34px] grid-cols-[minmax(190px,1.5fr)_120px_74px_100px_90px_108px] items-center gap-2 rounded-lg border border-[#eeeaf4] px-2 text-[8px]"
                >
                  <span class="flex min-w-0 items-center gap-2">
                    <span
                      class="grid size-6 shrink-0 place-items-center rounded-full bg-[#f3effc] text-[#5a24c7]"
                    >
                      <i class="i-lucide-user-round size-3.5" />
                    </span>
                    <strong class="truncate">{{ participant.name }}</strong>
                  </span>
                  <span class="truncate text-[#627096]">{{
                    participant.role
                  }}</span>
                  <span class="font-semibold text-[#5a24c7]">{{
                    participant.percentage
                  }}</span>
                  <span>{{ participant.amount }}</span>
                  <span>{{ participant.deduction }}</span>
                  <strong>{{ participant.netAmount }}</strong>
                </div>
                <div
                  class="mt-2 grid min-h-[34px] grid-cols-[minmax(190px,1.5fr)_120px_74px_100px_90px_108px] items-center gap-2 rounded-lg bg-[#faf9fd] px-2 text-[8px] text-[#27315d]"
                >
                  <strong class="col-span-2">Total</strong>
                  <strong class="text-[#5a24c7]">{{ rateioTotal }}</strong>
                  <strong>{{ commissionAmount }}</strong>
                  <strong>{{
                    sourceMoney([
                      'deductions_total',
                      'total_deductions',
                      'metadata.deductions_total',
                    ])
                  }}</strong>
                  <strong>{{ settledAmount }}</strong>
                </div>
              </div>
            </div>
            <div
              v-else
              class="mt-3 overflow-hidden rounded-xl border border-dashed border-[#ddd6eb] bg-[#faf9fd]"
            >
              <div
                class="grid min-w-[660px] grid-cols-[minmax(190px,1.5fr)_120px_74px_100px_90px_108px] gap-2 border-b border-[#eeeaf4] bg-violet-50/40 px-3 py-2 text-[7px] font-bold text-[#7a85a2]"
              >
                <span>Participante</span><span>Papel</span>
                <span>Percentual</span><span>Valor bruto</span>
                <span>Deduções</span><span>Valor líquido</span>
              </div>
              <div
                class="grid min-h-[116px] place-items-center bg-[#faf9fd] px-6 py-2 text-center"
              >
                <span class="w-full">
                  <i
                    class="i-lucide-users-round mx-auto size-5 text-[#a98bdd]"
                  />
                  <strong class="mt-1 block text-[9px] text-[#27315d]"
                    >Rateio indisponível</strong
                  >
                  <small class="block text-[8px] text-[#68749a]"
                    >A API deste acordo não retornou participantes.</small
                  >
                  <span class="mt-2 grid grid-cols-3 gap-2 text-left">
                    <span
                      class="rounded-lg border border-[#e8e4ef] bg-white/70 px-2 py-1"
                    >
                      <small class="block text-[7px] text-[#68749a]"
                        >Participantes</small
                      >
                      <strong class="block text-[8px] text-[#27315d]"
                        >Indisponível</strong
                      >
                    </span>
                    <span
                      class="rounded-lg border border-[#e8e4ef] bg-white/70 px-2 py-1"
                    >
                      <small class="block text-[7px] text-[#68749a]"
                        >Percentuais</small
                      >
                      <strong class="block text-[8px] text-[#27315d]"
                        >Indisponível</strong
                      >
                    </span>
                    <span
                      class="rounded-lg border border-[#e8e4ef] bg-white/70 px-2 py-1"
                    >
                      <small class="block text-[7px] text-[#68749a]"
                        >Valores</small
                      >
                      <strong class="block text-[8px] text-[#27315d]"
                        >Indisponível</strong
                      >
                    </span>
                  </span>
                </span>
              </div>
            </div>
          </article>
        </section>

        <aside
          data-imudei-visual-id="commission-edit-sidebar"
          class="space-y-2"
        >
          <article
            class="h-[340px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <h2 class="text-[13px] font-bold">Resumo das alterações</h2>
            <p class="mt-1 text-[8px] text-[#66739b]">
              {{
                hasRateioComparison
                  ? 'Confira como o rateio ficará após salvar as alterações.'
                  : 'Consulte a distribuição retornada pela API.'
              }}
            </p>
            <div class="mt-4 border-t border-[#eeeaf4] pt-3">
              <header class="flex items-center justify-between">
                <strong class="text-[9px]">
                  {{
                    hasRateioComparison
                      ? 'Comparativo do rateio'
                      : 'Distribuição atual'
                  }}
                </strong>
                <i class="i-lucide-chevron-up size-3.5" />
              </header>
              <div
                v-if="hasRateioComparison"
                class="mt-3 flex items-center gap-3 text-[7px] text-[#68749a]"
              >
                <span class="flex items-center gap-1.5"
                  ><i
                    class="h-1.5 w-5 rounded-full bg-[#dddce8]"
                  />Anterior</span
                >
                <span class="flex items-center gap-1.5"
                  ><i class="h-1.5 w-5 rounded-full bg-[#5a24c7]" />Novo</span
                >
              </div>
              <small
                v-else-if="participants.length"
                class="mt-3 block text-[7px] text-[#68749a]"
              >
                Percentuais atuais retornados pela API.
              </small>
              <div v-if="participants.length" class="mt-3 space-y-3">
                <div
                  v-for="participant in participants.slice(0, 4)"
                  :key="participant.id"
                  class="text-[8px]"
                >
                  <span class="flex items-center justify-between gap-2">
                    <strong class="truncate">{{ participant.name }}</strong>
                    <span
                      v-if="participant.hasComparison"
                      class="flex items-center gap-2"
                    >
                      <small class="text-[#68749a]">{{
                        participant.previousPercentage
                      }}</small>
                      <b class="text-[#5a24c7]">{{
                        participant.currentPercentage
                      }}</b>
                    </span>
                    <b v-else class="text-[#5a24c7]">
                      {{ participant.currentPercentage }}
                    </b>
                  </span>
                  <small class="mt-1 block truncate text-[#68749a]">{{
                    participant.role
                  }}</small>
                  <span
                    v-if="participant.hasComparison"
                    class="mt-1 grid grid-cols-2 gap-2"
                  >
                    <i class="h-1.5 overflow-hidden rounded-full bg-[#dddce8]">
                      <i
                        class="block h-full rounded-full bg-[#b8b3ca]"
                        :class="
                          percentageBarWidth(
                            participant.previousPercentageValue
                          )
                        "
                      />
                    </i>
                    <i class="h-1.5 overflow-hidden rounded-full bg-violet-100">
                      <i
                        class="block h-full rounded-full bg-[#5a24c7]"
                        :class="
                          percentageBarWidth(participant.currentPercentageValue)
                        "
                      />
                    </i>
                  </span>
                  <small
                    v-if="participant.hasComparison"
                    class="mt-1 block text-right text-[7px]"
                    :class="
                      percentageDelta(participant).startsWith('+')
                        ? 'text-emerald-600'
                        : percentageDelta(participant).startsWith('-')
                          ? 'text-rose-500'
                          : 'text-[#68749a]'
                    "
                  >
                    {{ percentageDelta(participant) }}
                  </small>
                  <template v-else-if="participant.currentPercentage === '—'">
                    <small class="mt-1 block text-[#8b95ae]"
                      >Percentual não informado</small
                    >
                  </template>
                </div>
              </div>
              <div
                v-else
                class="mt-3 grid min-h-[220px] grid-rows-[1fr_auto] rounded-xl border border-dashed border-[#e3ddec] bg-[#faf9fd] p-3 text-center"
              >
                <span class="grid place-items-center">
                  <i
                    class="i-lucide-chart-no-axes-combined mx-auto size-5 text-[#a98bdd]"
                  />
                  <strong class="mt-2 block text-[9px]"
                    >Comparativo indisponível</strong
                  >
                  <small class="mt-1 block text-[8px] text-[#68749a]"
                    >A API não retornou a distribuição dos participantes.</small
                  >
                </span>
                <span class="grid grid-cols-2 gap-2 text-left">
                  <span
                    class="rounded-lg border border-[#e8e4ef] bg-white/70 px-2 py-1.5"
                  >
                    <small class="block text-[7px] text-[#68749a]"
                      >Participantes</small
                    >
                    <strong class="block text-[8px] text-[#27315d]"
                      >Não retornados</strong
                    >
                  </span>
                  <span
                    class="rounded-lg border border-[#e8e4ef] bg-white/70 px-2 py-1.5"
                  >
                    <small class="block text-[7px] text-[#68749a]"
                      >Rateio</small
                    >
                    <strong class="block text-[8px] text-[#27315d]"
                      >Indisponível</strong
                    >
                  </span>
                </span>
              </div>
            </div>
          </article>

          <article
            class="h-[106px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <h2 class="text-[11px] font-bold">Conquistas recentes</h2>
            <div class="mt-3 grid grid-cols-3 gap-2 text-center">
              <span>
                <i
                  class="mx-auto grid size-7 place-items-center rounded-full border border-violet-200 bg-violet-50 text-[9px] font-bold text-[#5a24c7]"
                  >—</i
                >
                <small class="mt-1 block truncate text-[7px] text-[#68749a]"
                  >Rateios</small
                >
              </span>
              <span>
                <i
                  class="mx-auto grid size-7 place-items-center rounded-full border border-orange-200 bg-orange-50 text-[9px] font-bold text-orange-600"
                  >—</i
                >
                <small class="mt-1 block truncate text-[7px] text-[#68749a]"
                  >Aprovações</small
                >
              </span>
              <span>
                <i
                  class="mx-auto grid size-7 place-items-center rounded-full border border-amber-200 bg-amber-50 text-[9px] font-bold text-amber-600"
                  >—</i
                >
                <small class="mt-1 block truncate text-[7px] text-[#68749a]"
                  >Documentos</small
                >
              </span>
            </div>
          </article>

          <article
            ref="auditPanel"
            class="h-[279px] overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <h2 class="text-[12px] font-bold">Trilha de auditoria</h2>
            <div v-if="audit.length" class="mt-2 space-y-3">
              <div
                v-for="(item, index) in audit.slice(0, 3)"
                :key="item.id"
                class="relative pl-5"
              >
                <i
                  v-if="index < audit.slice(0, 3).length - 1"
                  class="absolute left-[4px] top-3 h-[calc(100%+12px)] w-px bg-[#e8e4ef]"
                />
                <i
                  class="absolute left-0 top-1.5 size-2 rounded-full"
                  :class="index === 0 ? 'bg-emerald-500' : 'bg-[#5a24c7]'"
                />
                <small class="text-[7px] text-[#68749a]">{{ item.date }}</small>
                <div class="mt-1 rounded-lg bg-[#faf9fd] p-2">
                  <strong class="block text-[8px]">{{ item.label }}</strong>
                  <span class="mt-2 flex items-center gap-2">
                    <span
                      class="grid size-5 place-items-center rounded-full bg-[#f3effc] text-[#5a24c7]"
                    >
                      <i class="i-lucide-user-round size-3" />
                    </span>
                    <small class="min-w-0 flex-1 truncate text-[7px]">{{
                      item.actor
                    }}</small>
                    <em
                      class="rounded bg-violet-100 px-1.5 py-1 text-[7px] not-italic text-[#5a24c7]"
                    >
                      {{ item.status }}
                    </em>
                  </span>
                </div>
              </div>
              <div v-if="audit.length < 3" class="relative min-h-[52px] pl-5">
                <i
                  class="absolute left-0 top-1.5 size-2 rounded-full bg-[#c8c3d4]"
                />
                <small class="text-[7px] text-[#68749a]"
                  >Registros adicionais</small
                >
                <div
                  class="mt-1 rounded-lg border border-dashed border-[#e3ddec] bg-[#faf9fd] p-2 text-[7px] text-[#68749a]"
                >
                  A API retornou apenas {{ audit.length }} registro<span
                    v-if="audit.length !== 1"
                    >s</span
                  >.
                </div>
              </div>
            </div>
            <div
              v-else
              class="grid min-h-[190px] place-items-center rounded-xl border border-dashed border-[#e3ddec] bg-[#faf9fd] text-center"
            >
              <span>
                <i class="i-lucide-file-clock mx-auto size-5 text-[#a98bdd]" />
                <strong class="mt-2 block text-[9px]"
                  >Histórico indisponível</strong
                >
                <small class="mt-1 block text-[8px] text-[#68749a]"
                  >A API não retornou versões para este acordo.</small
                >
              </span>
            </div>
          </article>
        </aside>
      </section>
    </template>

    <footer
      data-imudei-visual-id="commission-edit-footer"
      class="fixed bottom-6 left-[194px] right-0 z-50 flex h-[55px] items-center gap-4 border-t border-[#e8e4ee] bg-white/95 shadow-[0_-8px_25px_rgba(38,25,86,0.04)] backdrop-blur xl:block xl:left-[221px] xl:right-[21px]"
    >
      <button
        class="h-10 min-w-[120px] rounded-lg !border !border-solid !border-[#8e65ef] bg-white text-[10px] font-medium text-[#5a24c7] xl:absolute xl:bottom-0 xl:left-[2px]"
        type="button"
        @click="returnToDashboard"
      >
        Cancelar
      </button>
      <span
        class="mx-auto hidden w-[368px] shrink-0 items-center gap-3 rounded-lg border border-[#cfc2ff] bg-violet-50 px-4 py-2 text-[8px] text-[#66739b] xl:absolute xl:bottom-0 xl:left-[338px] xl:mx-0 xl:flex"
      >
        <i class="i-lucide-shield-check size-5 text-[#5a24c7]" />
        <span
          ><strong class="block text-[#5a24c7]"
            >Segurança e responsabilidade</strong
          >Valores maiores, alterações repetidas e duplicidades não geram mais
          pontos.</span
        >
      </span>
      <div
        class="ml-auto flex items-center gap-4 xl:absolute xl:bottom-0 xl:right-6 xl:ml-0"
      >
        <button
          v-if="!availableActions.length"
          aria-label="Salvar rascunho indisponível"
          class="flex h-10 min-w-[162px] items-center justify-center rounded-lg !border !border-solid !border-[#8e65ef] bg-white text-[10px] font-semibold text-[#5a24c7] disabled:opacity-100"
          disabled
          type="button"
        >
          Salvar rascunho
        </button>
        <button
          v-if="!availableActions.length"
          aria-label="Salvar alterações indisponível"
          class="flex h-10 min-w-[204px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#5d18d2] to-[#4a0ca8] text-[10px] font-bold text-white disabled:opacity-100"
          disabled
          type="button"
        >
          <i class="i-lucide-lock-keyhole size-3.5" />
          Salvar alterações
        </button>
        <button
          v-for="action in availableActions"
          :key="action.name"
          class="flex h-10 min-w-[156px] items-center justify-center gap-2 rounded-lg text-[10px] font-bold transition disabled:cursor-not-allowed disabled:opacity-70"
          :class="
            action.name === 'publishAgreement'
              ? 'bg-gradient-to-r from-[#5d18d2] to-[#4a0ca8] text-white'
              : 'border border-[#9f79ee] text-[#5a24c7]'
          "
          :disabled="saving"
          type="button"
          @click="performAction(action.name)"
        >
          <i
            :class="
              action.name === 'publishAgreement'
                ? 'i-lucide-send size-4'
                : 'i-lucide-circle-check-big size-4'
            "
          />
          {{ saving ? 'Processando…' : actionLabel(action.name) }}
        </button>
      </div>
    </footer>
  </main>
</template>
